let started = false;
let networkDebugInstalled = false;
let analyticsInitPromise: Promise<void> | null = null;

type TelegramAnalyticsSdk = typeof import('@telegram-apps/analytics').default;
let analyticsSdk: TelegramAnalyticsSdk | null = null;

function analyticsDebugEnabled(): boolean {
  const fromQuery = new URLSearchParams(window.location.search).has('analytics_debug');
  const fromStartParam = window.Telegram?.WebApp?.initDataUnsafe?.start_param === 'analytics_debug';
  return fromQuery || fromStartParam;
}

function installAnalyticsNetworkDebug(): void {
  if (networkDebugInstalled || !analyticsDebugEnabled()) return;
  networkDebugInstalled = true;
  const originalFetch = window.fetch.bind(window);
  const events: string[] = [];
  window.__blinkrAnalyticsNetwork = events;

  window.fetch = async (input, init) => {
    const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
    const isAnalytics = url.includes('tganalytics.xyz');
    if (!isAnalytics) return originalFetch(input, init);

    const method = init?.method ?? 'GET';
    const startedAt = performance.now();
    try {
      const response = await originalFetch(input, init);
      events.push(`${method} ${url.replace(/\?.*$/, '')} -> ${response.status} ${Math.round(performance.now() - startedAt)}ms`);
      return response;
    } catch (err) {
      events.push(`${method} ${url.replace(/\?.*$/, '')} -> ${err instanceof Error ? err.message : String(err)}`);
      throw err;
    }
  };
}

function showAnalyticsDebug(): void {
  if (!analyticsDebugEnabled()) return;
  window.setTimeout(() => {
    const state = window.__blinkrAnalytics ?? {
      appName: 'unknown',
      env: 'PROD',
      hasToken: false,
      status: 'failed',
      error: 'Telegram Analytics init was not called',
    };
    const network = window.__blinkrAnalyticsNetwork ?? [];
    window.alert(`Blinkr analytics\n${JSON.stringify(state, null, 2)}\n\nNetwork\n${network.length ? network.join('\n') : 'No tganalytics requests captured'}`);
  }, 9_000);
}

export function initTelegramAnalytics(): void {
  installAnalyticsNetworkDebug();
  showAnalyticsDebug();
  if (started) return;
  const token = import.meta.env.VITE_TG_ANALYTICS_TOKEN?.trim();
  if (!token) {
    window.__blinkrAnalytics = {
      appName: (import.meta.env.VITE_TG_ANALYTICS_APP_NAME?.trim() || 'blinkr').toLowerCase(),
      env: import.meta.env.VITE_TG_ANALYTICS_ENV === 'STG' ? 'STG' : 'PROD',
      hasToken: false,
      status: 'failed',
      error: 'VITE_TG_ANALYTICS_TOKEN is empty',
    };
    return;
  }

  started = true;
  const appName = (import.meta.env.VITE_TG_ANALYTICS_APP_NAME?.trim() || 'blinkr').toLowerCase();
  const env = import.meta.env.VITE_TG_ANALYTICS_ENV === 'STG' ? 'STG' : 'PROD';
  window.__blinkrAnalytics = { appName, env, hasToken: true, status: 'starting' };

  try {
    analyticsInitPromise = import('@telegram-apps/analytics')
      .then(({ default: telegramAnalytics }) => {
        analyticsSdk = telegramAnalytics;
        return telegramAnalytics.init({ token, appName, env });
      })
      .then(() => {
        window.__blinkrAnalytics = { appName, env, hasToken: true, status: 'ready' };
      })
      .catch((err) => {
        started = false;
        window.__blinkrAnalytics = {
          appName,
          env,
          hasToken: true,
          status: 'failed',
          error: err instanceof Error ? err.message : String(err),
        };
        console.warn('Telegram Analytics init failed', err);
      });
  } catch (err) {
    started = false;
    window.__blinkrAnalytics = {
      appName,
      env,
      hasToken: true,
      status: 'failed',
      error: err instanceof Error ? err.message : String(err),
    };
    console.warn('Telegram Analytics init failed', err);
  }
}

export function warmTelegramAnalytics(): void {
  const idle = (globalThis as typeof globalThis & {
    requestIdleCallback?: (callback: () => void, options?: { timeout?: number }) => number;
  }).requestIdleCallback;
  if (typeof idle === 'function') {
    idle(() => initTelegramAnalytics(), { timeout: 2500 });
    return;
  }
  globalThis.setTimeout(() => initTelegramAnalytics(), 700);
}

export function trackTelegramEvent(name: string, params: Record<string, unknown> = {}): void {
  const event = {
    name,
    params,
    at: new Date().toISOString(),
  };
  window.__blinkrAnalyticsEvents = [...(window.__blinkrAnalyticsEvents ?? []), event].slice(-100);

  if (!started) {
    warmTelegramAnalytics();
  }

  void analyticsInitPromise?.then(() => {
    // @telegram-apps/analytics currently exposes page/session and invoice
    // tracking publicly. Keep product events in a stable local buffer so a
    // future SDK custom-event method can be wired here without touching UI code.
    void analyticsSdk;
  });
}
